import React, { FC, useContext } from 'react'

import { InfoCircleOutlined, PlusCircleFilled } from '@ant-design/icons'
import { LayoutRowWithActions } from '../../../../../../components/data-control-row/layout-row-with-actions'
import { BlueConicBoost } from '@pushly/models/lib/structs/journeys'
import { BlueConicAlgorithm, BlueConicAlgorithmRampUp } from '@pushly/models/lib/enums'
import { getEnumKeys, getEnumValues } from '@pushly/models'
import {
    BLUECONIC_BOOST_DISPLAY_NAMES,
    DEFAULT_BLUECONIC_BOOST_VALUE,
    ORDERED_BLUECONIC_ALGORITHM_RAMP_UP_OPTIONS,
} from '../constants'
import { BlueConicConfigurationContext } from '../configuration/context'
import { BlueConicConfigurationDispatchContext } from '../configuration/reducer'
import './blueconic-boosts-editor.scss'

import titleCase from 'title-case'
import { alphaSort } from '../../../../../../_utils/array'
import { EditableViewContext } from '@pushly/aqe/lib/components/hoc/editable-view/editable-view-context'
import classnames from 'classnames'
import { Button, Card, Flex, Popover, Select, Slider, Space } from 'antd'
import { Well } from '@pushly/aqe/lib/components'

export const BlueConicBoostsEditor = () => {
    const viewCtx = useContext(EditableViewContext)
    const isReadonly = viewCtx.mode === 'view'
    const context = useContext(BlueConicConfigurationContext)
    const dispatch = useContext(BlueConicConfigurationDispatchContext)

    const currBoosts = context.boosts
    const nextBoost = getEnumValues(BlueConicAlgorithm)
        .filter((a) => !currBoosts.find((b) => b.algorithm === a))
        .map((a) => a)[0]

    return (
        <Well
            type="inner"
            padding="none"
            className="blueconic-boosts-editor"
            size="small"
            title={
                <div className="boosts-header">
                    <div className="boosts-label">
                        Boosts{' '}
                        <span className="size-label">
                            ({currBoosts.length}/{getEnumKeys(BlueConicAlgorithm).length})
                        </span>
                        <Popover
                            classNames={{ content: 'blueconic-boosts-header-popover' }}
                            content={
                                <div>
                                    <p>
                                        Set at least one boost algorithm to be used in recommendations.
                                        <br />
                                        <br />
                                        <b>Ramp Up</b>: Controls how quickly this algorithm reaches full influence in
                                        recommendations. Instant applies it immediately; slower settings phase it in
                                        gradually, giving the model more time to learn before it dominates results.
                                        <br />
                                        <br />
                                        <b>Value</b>: Controls the weight of the algorithm when used to match
                                        recommendations.
                                    </p>
                                </div>
                            }
                            trigger="hover"
                        >
                            <InfoCircleOutlined className="info-icon" aria-hidden="true" />
                        </Popover>
                    </div>
                </div>
            }
            styles={{
                header: { marginBottom: 0 },
                body: { padding: '0 8px 8px' },
                root: { background: 'rgba(118, 178, 216, 0.1)', borderColor: 'rgba(118, 178, 216, 0.25)' },
            }}
        >
            <Flex vertical>
                {currBoosts.map((boost) => (
                    <LayoutRowWithActions
                        key={boost.algorithm}
                        readonly={isReadonly}
                        dataItem={<BlueConicAlgorithmBuilder key={boost.algorithm} boost={boost} />}
                        onDelete={() => {
                            if (currBoosts.length === 1) {
                                return
                            }
                            dispatch({ type: 'remove-boost', boost })
                        }}
                    />
                ))}
                {!isReadonly && nextBoost && (
                    <Button
                        type="primary"
                        size="small"
                        style={{ width: '30px', strokeWidth: '2' }}
                        icon={<PlusCircleFilled />}
                        disabled={!nextBoost}
                        onClick={() => {
                            if (!nextBoost) return

                            const boostToAdd = BlueConicBoost.from({
                                algorithm: nextBoost,
                                value: DEFAULT_BLUECONIC_BOOST_VALUE,
                            })

                            if (
                                [BlueConicAlgorithm.COLLABORATIVE_FILTERING, BlueConicAlgorithm.INTEREST].includes(
                                    boostToAdd.algorithm,
                                )
                            ) {
                                boostToAdd.rampUp = BlueConicAlgorithmRampUp.INSTANT
                            }

                            dispatch({ type: 'add-boost', boost: boostToAdd })
                        }}
                    />
                )}
            </Flex>
        </Well>
    )
}

type BlueConicAlgorithmBuilderProps = {
    boost: BlueConicBoost
}

const BlueConicAlgorithmBuilder: FC<BlueConicAlgorithmBuilderProps> = ({ boost }) => {
    const viewCtx = useContext(EditableViewContext)
    const context = useContext(BlueConicConfigurationContext)
    const dispatch = useContext(BlueConicConfigurationDispatchContext)

    const setBoosts = context.boosts.map((setBoost) => setBoost.algorithm)
    const hasRampUp = [BlueConicAlgorithm.COLLABORATIVE_FILTERING, BlueConicAlgorithm.INTEREST].includes(
        boost.algorithm,
    )

    return (
        <Space.Compact className="blueconic-algorithm-builder" size="middle" block>
            <Space.Addon>Algorithm</Space.Addon>
            <Select<BlueConicAlgorithm>
                className={classnames(viewCtx.modeClassName, 'blueconic-algorithm-select')}
                size="middle"
                options={getEnumValues(BlueConicAlgorithm)
                    .filter((algorithm) => algorithm === boost.algorithm || !setBoosts.includes(algorithm))
                    .map((b) => ({
                        value: b,
                        label: BLUECONIC_BOOST_DISPLAY_NAMES[b],
                    }))
                    .sort(({ label: aLabel }, { label: bLabel }) => alphaSort(aLabel, bLabel))}
                placeholder="Select an algorithm"
                value={boost.algorithm}
                onChange={(value) => {
                    dispatch({ type: 'update-boost', boost, change: boost.clone({ algorithm: value }) })
                }}
                filterOption={false}
                showSearch={false}
            />

            {hasRampUp && (
                <>
                    <Space.Addon>Ramp Up</Space.Addon>
                    <Select<BlueConicAlgorithmRampUp>
                        className={classnames(viewCtx.modeClassName, 'blueconic-ramp-up-select')}
                        value={boost.rampUp}
                        size="middle"
                        options={ORDERED_BLUECONIC_ALGORITHM_RAMP_UP_OPTIONS.map((rampUp) => ({
                            label: titleCase(rampUp),
                            value: rampUp,
                        }))}
                        onChange={(value) =>
                            dispatch({ type: 'update-boost', boost, change: boost.clone({ rampUp: value }) })
                        }
                        filterOption={false}
                        showSearch={false}
                    />
                </>
            )}

            <Space.Addon>
                Value: <b>{parseFloat(boost.value)}</b>
            </Space.Addon>
            <div className={classnames('blueconic-algorithm-value-slider', viewCtx.modeClassName)}>
                <Slider
                    className={classnames(viewCtx.modeClassName)}
                    key={`${boost.algorithm}_slider`}
                    defaultValue={parseFloat(boost.value)}
                    min={1}
                    max={10}
                    step={0.5}
                    onChangeComplete={(value) => {
                        dispatch({ type: 'update-boost', boost, change: boost.clone({ value: String(value) }) })
                    }}
                />
            </div>
        </Space.Compact>
    )
}
